// ðŸ”§ ballot.js
// âœ… Purpose: Display the user's submitted votes
// âœ… Features:
// - Shows one vote per category, most recent shown first
// - Allows filtering by category
// - Provides print-friendly view of ballot
// - Highlights vote just submitted (via ?voted)
// - Shows cancel message if redirected with ?cancel
// - âœ… Displays Best in Show votes at top (if enabled)

import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
} from "firebase/firestore";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useEventContext } from "../../hooks/use_event_context";
import { useCategories } from "../../hooks/use_categories";
import { useVoteConfirmation } from "../../hooks/use_vote_confirmation";
import FullPageSpinner from "../../components/FullPageSpinner";
import RequireEventId from "../../components/RequireEventId";
import { useVotingStatus } from "../../hooks/use_voting_status";
import { logger } from '../../utils/logger';
import { getEventVotingType } from "../../utils/event_voting_type_helpers";
import { showStatus } from "../../utils/status_helpers";
import {
  getVoteUiMode,
  getLeaderboardAggregationMode,
} from "../../utils/vote_behavior_helpers";
import {
  getVotingTypeLabel,
  isJudgedVotingFamily,
  isSelectionVotingFamily,
} from "../../utils/voting_engine_registry";
import {
  getSessionValue,
  removeSessionValue,
} from "../../utils/browser_storage_helpers";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const Ballot = () => {

  const [user, loading] = useAuthState(auth);

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [votes, setVotes] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [volunteerSuccess, setVolunteerSuccess] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showCloseMessage, setShowCloseMessage] = useState(false);
  const [showCancelMessage, setShowCancelMessage] = useState(false);
  const [bestInShowEnabled, setBestInShowEnabled] = useState(false);
  const [upcomingVolunteerSlot, setUpcomingVolunteerSlot] = useState(null);
  const [expandedScorecards, setExpandedScorecards] = useState({});
  const [searchParams] = useSearchParams();
  const showCloseTabButton = getSessionValue("qrVoteTab") === "true";
  const voteConfirmation = useVoteConfirmation();
  const { categories, categoriesMap, loading: catsLoading } = useCategories(eventId);
  const [votingSettings, setVotingSettings] = useState({});
  const { isVotingOpen, loading: votingLoading } = useVotingStatus(eventId);
  const navigate = useNavigate();
  const votingType = getEventVotingType(eventDoc);
  const voteUiMode = getVoteUiMode(eventDoc);
  const aggregationMode = getLeaderboardAggregationMode(eventDoc);
  const votingTypeLabel = getVotingTypeLabel(votingType);
  const isSelectionVoting =
    isSelectionVotingFamily(votingType);
  
  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {

    logger.log("ðŸ—³ Ballot voting configuration", {
      votingType,
      voteUiMode,
      aggregationMode,
    });

  }, [votingType]);

  // ðŸ§¹ Clean Volunteer Acceptance
  useEffect(() => {
    if (!user) return;

    const key = `volunteerAccepted_${user.uid}`;

    if (getSessionValue(key) === "true") {
      setVolunteerSuccess(true);

      removeSessionValue(key);

      const timer = setTimeout(() => {
        setVolunteerSuccess(false);
      }, 20000);

      return () => clearTimeout(timer);
    }
  }, [user]);

  // ðŸ§¹ Canceled vote message
  useEffect(() => {
    if (searchParams.get("cancel") === "1") {
      setShowCancelMessage(true);
      setTimeout(() => setShowCancelMessage(false), 15000);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      if (!ready || !user || !eventId || loading || catsLoading) return;

      try {
        const settingsRef = doc(db, "events", eventId, "settings", "voting");
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const settings = settingsSnap.data();
          setVotingSettings(settings);
          setBestInShowEnabled(settings.bestInShowEnabled === true);
        }

        const q = query(
          collection(db, "events", eventId, "votes"),
          where("uid", "==", user.uid),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);

        // =====================================================
        // ðŸš— Load all event entries once, then enrich votes locally
        // =====================================================
        // This avoids one Firestore read per vote.
        // Better for events with 200+ entries.

        const carsSnap = await getDocs(
          collection(db, "events", eventId, "cars")
        );

        const carsMap = {};

        carsSnap.docs.forEach((carDoc) => {
          const car = {
            id: carDoc.id,
            ...carDoc.data(),
          };

          carsMap[carDoc.id] = car;
        });

        const voteList = snap.docs.map((docSnap) => {
          const vote = {
            id: docSnap.id,
            ...docSnap.data(),
          };

          const car = carsMap[vote.cid];

          if (!car) {
            logger.warn("âš ï¸ Car not found for vote:", {
              value: vote.cid,
            });

            return vote;
          }

          return {
            ...vote,
            regcnt: car.regcnt,
            description: car.description,
            name:
              `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim() ||
              car.ownerName ||
              "Unknown Owner",
          };
        });

        setVotes(voteList);

      } catch (err) {
        logger.error("âŒ Error loading ballot:", {
          value: err
        });

        showStatus(setStatus, {
          type: "error",
          message: "Error loading your ballot.",
        });

        setError("Error loading your ballot.");
      }
    };

    fetchData();
  }, [ready, user, eventId, loading, catsLoading]);

  useEffect(() => {
    if (!ready || !eventId || !user) return;

    trackEvent({
      action: GA_EVENTS.BALLOT_VIEWED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        voting_type: votingType,
        source_page: window.location.pathname,
      },
    });
  }, [ready, eventId, eventslug, user, votingType]);

  useEffect(() => {
    if (!eventId || !user) return;
  
    const fetchVolunteerSlots = async () => {
      try {
        const activitiesSnap = await getDocs(collection(db, "events", eventId, "volunteers"));
        const now = new Date();
        const twelveHoursFromNow = new Date(now.getTime() + 12 * 60 * 60 * 1000);
  
        for (const actDoc of activitiesSnap.docs) {
          const activity = actDoc.data();
          const slotSnap = await getDocs(
            collection(db, "events", eventId, "volunteers", actDoc.id, "timeSlots")
          );
  
          for (const slotDoc of slotSnap.docs) {
            const slot = slotDoc.data();
            const toDate = (val) => {
              if (!val) return null;
              if (val.toDate) return val.toDate();        // Firestore Timestamp
              if (typeof val === "string") return new Date(val); // ISO string
              return new Date(val);                       // fallback (Date object)
            };
            
            const slotStart = toDate(slot.startTime);
            const slotEnd = toDate(slot.endTime);
            
            const isUpcoming =
              slot.assignedUserIds?.includes(user.uid) &&
              slotStart >= now &&
              slotStart <= twelveHoursFromNow;
  
            if (isUpcoming) {
              setUpcomingVolunteerSlot({
                startTime: slotStart,
                endTime: slotEnd,
                activityTitle: activity.title || "Volunteer Task",
              });
              return;
            }
          }
        }
      } catch (err) {

        logger.warn("âš ï¸ Could not check volunteer duties:", {
          value: err
        });

        showStatus(setStatus, {
          type: "warning",
          message: "Unable to load volunteer duty information.",
        });

      }
    };
  
    fetchVolunteerSlots();
  }, [eventId, user]);

  useEffect(() => {
    if (
      !loading &&
      user &&
      !user.emailVerified
    ) {
      navigate(
        "/dashboard?verifyRequired=1",
        { replace: true }
      );
    }
  }, [loading, user, navigate]);

  if (eventLoading || !ready) {
    return <FullPageSpinner message="Loading your ballot..." />;
  }
  if (error) return <div className="container mt-5 text-danger">❌ {error}</div>;
  if (!eventId) return <div className="container mt-5 text-danger">❌ Event not found.</div>;

  const submittedCount = new Set(votes.map(v => v.catid)).size;
  const totalCount = categories.length;

const getCategoryName = (cat) => {
  return cat?.name || cat?.category || "Unnamed Category";
};

const sortedCategories = selectedCategory
  ? categories.filter(
      (cat) =>
        String(cat.id) === String(selectedCategory) &&
        String(cat.id) !== "0"
    )
  : [...categories]
      .filter((cat) => String(cat.id) !== "0")
      .sort((a, b) => {
        const sortA = Number.isFinite(Number(a?.sortOrder))
          ? Number(a.sortOrder)
          : Number.isFinite(Number(a?.id))
          ? Number(a.id)
          : 9999;

        const sortB = Number.isFinite(Number(b?.sortOrder))
          ? Number(b.sortOrder)
          : Number.isFinite(Number(b?.id))
          ? Number(b.id)
          : 9999;

        if (sortA !== sortB) {
          return sortA - sortB;
        }

        return getCategoryName(a).localeCompare(
          getCategoryName(b),
          undefined,
          { sensitivity: "base" }
        );
      });

  const votesByCat = {};
  votes.forEach((v) => {
    if (!votesByCat[String(v.catid)]) {
      votesByCat[String(v.catid)] = v;
    }
  });

  const bestInShowVotes = bestInShowEnabled
    ? votes.filter(v => v?.bestInShow).sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds)
    : [];
  const bestInShowVote = bestInShowVotes[0];

  const now = new Date();
  const start = votingSettings?.votingStart?.toDate?.();
  const end = votingSettings?.votingEnd?.toDate?.();

  const pointsVotesGroupedByCategory = sortedCategories
  .map((cat) => {
    const categoryVotes = votes
      .filter((vote) => String(vote.catid) === String(cat.id))
      .sort(
        (a, b) =>
          Number(b.totalPoints || 0) -
          Number(a.totalPoints || 0)
      );

    return {
      cat,
      categoryName: getCategoryName(cat),
      votes: categoryVotes,
    };
  })
  .filter((group) => group.votes.length > 0);

  const toggleScorecard = (voteId) => {
  setExpandedScorecards((prev) => ({
    ...prev,
    [voteId]: !prev[voteId],
  }));

};

return (
  <RequireEventId>
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">Your Votes</h1>

          <p className="lead text-muted mb-1">
            {votingTypeLabel}
          </p>

          <p className="text-muted">
            {isSelectionVoting
              ? "One vote per category. Most recent shown first."
              : "Your submitted scorecards and judging results."}
          </p>

        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {upcomingVolunteerSlot && (
          <div className="alert alert-warning fade-in" style={{ backgroundColor: "#fff8dc", borderColor: "#fce299" }}>
            ⚠️ You have a volunteer shift coming up:
            <br />
            <strong>{upcomingVolunteerSlot.activityTitle}</strong> —{" "}
            {upcomingVolunteerSlot.startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} to{" "}
            {upcomingVolunteerSlot.endTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            <br />
            Please check in with the event staff before your shift begins.
            <br />
            👉 <a href={`/${eventslug}/volunteering`} className="text-decoration-underline">View your volunteer duties</a>
          </div>
        )}

        {/* ðŸ”’ Voting status */}
        {!votingLoading && isVotingOpen === false && (
          <div className="alert alert-danger mt-3">
            🚫 Voting is currently closed.
            {start && <> <br /> Voting opens: <strong>{start.toLocaleString()}</strong> </>}
            {end && <> <br /> Voting closes: <strong>{end.toLocaleString()}</strong> </>}
          </div>
        )}

        {/* âœ… Vote Confirmation */}
        {/* âœ… Vote Confirmation */}
        {voteConfirmation && (
          <div className="alert alert-success fade-out">

            <strong>✅ Vote Submitted Successfully</strong>

            <div className="mt-2">
              <strong>
                {categoriesMap?.[String(voteConfirmation.catid)] ||
                  "Unknown Category"}
              </strong>
              {" — "}
              <strong>
                #{votesByCat[voteConfirmation.catid]?.regcnt}
                {" - "}
                {votesByCat[voteConfirmation.catid]?.description}
              </strong>
              {" by "}
              <strong>
                {votesByCat[voteConfirmation.catid]?.name}
              </strong>
            </div>

            <div className="small text-muted mt-2">
              Voted at:{" "}
              {new Date(
                voteConfirmation.createdAt
              ).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>

            {showCloseTabButton && (
              <>
                <hr />

                <div className="mb-2">
                  📱 If you opened this page by scanning a QR code, you can now
                  safely close this tab and continue enjoying the event.
                </div>

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => {
                    logger.log(
                      "ðŸšª User clicked Close This Voting Tab"
                    );
                    
                    removeSessionValue("qrVoteTab");
                    window.close();

                    setTimeout(() => {
                      setShowCloseMessage(true);
                    }, 500);
                  }}
                >
                  Close This Voting Tab
                </button>
              </>
            )}

          </div>
        )}

        {showCloseMessage && (
          <div className="alert alert-info fade-in">
            🙌 Thank you for voting!<br />
            You can now close this tab and scan your next item.
          </div>
        )}

        {showCancelMessage && (
          <div className="alert alert-warning fade-in">
            ❌ Your vote was canceled.<br />
            You can now close this tab and scan the next item.
          </div>
        )}

        {isVotingOpen && (
          <button
            className="btn btn-primary mb-3"
            onClick={() => navigate(`/${eventslug}/manual_vote`)}
          >
            Vote Manually
          </button>
        )}

        {/* âœ… Volunteer success */}
        {volunteerSuccess && (
          <div className="alert alert-success fade-in">
            🎉 You’ve been added to your volunteer time slot!
          </div>
        )}

        {/* ðŸ”§ Filter Toolbar */}
        {isVotingOpen && (
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4 print:hidden">
            <div className="text-muted small">
              You’ve voted in {submittedCount} of {totalCount} categories
            </div>
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="form-select"
              >
                <option value="">All Categories</option>
                {categories.filter(cat => String(cat.id) !== "0").map(cat => (

                  <option key={cat.id} value={cat.id}>
                    {getCategoryName(cat)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button onClick={() => window.print()} className="btn btn-light">
                🖨️ Print My Ballot
              </button>
            </div>
          </div>
        )}

        {/* ðŸ—³ Ranked Choice Ballot */}
        {votingType === "ranked_choice" && (
          <div className="printable-area mt-4">

            <div className="alert alert-info mb-4">
              <strong>🗳️ Your Ranked Choice Ballot</strong>
              <br />
              Rankings are grouped by category. Lower rank numbers are higher preference.
            </div>

            {sortedCategories.map((cat) => {
              const vote = votesByCat[String(cat.id)];

              return (
                <div key={cat.id} className="mb-4">

                  <h4 className="mb-2">{getCategoryName(cat)}</h4>

                  <div className="list-group">

                    {!vote?.rankings?.length ? (
                      <div className="list-group-item">
                        <div className="text-danger">
                          ⚠️ No rankings submitted for this category.
                        </div>
                      </div>
                    ) : (
                      [...vote.rankings]
                        .sort((a, b) => Number(a.rank) - Number(b.rank))
                        .slice(0, 5)
                        .map((ranking) => (
                          <div
                            key={`${cat.id}-${ranking.rank}-${ranking.cid}`}
                            className="list-group-item"
                          >
                            <div className="row align-items-center g-3">

                              <div className="col-12 col-md-2">
                                <span className="badge bg-primary fs-6">
                                  Rank #{ranking.rank}
                                </span>
                              </div>

                              <div className="col-12 col-md-7">
                                <strong>
                                  #{ranking.regcnt} — {ranking.description}
                                </strong>

                                <div className="small text-muted">
                                  {ranking.name || "Unknown Owner"}
                                </div>
                              </div>

                              <div className="col-12 col-md-3 text-md-end">
                                <Link
                                  to={`/${eventslug}/vote/${ranking.cid}`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View / Update
                                </Link>
                              </div>

                            </div>
                          </div>
                        ))
                    )}

                  </div>
                </div>
              );
            })}

          </div>
        )}

        {/* ðŸ† Judged Points Voting Ballot */}
        {votingType === "points" && (
          <div className="printable-area mt-4">

            <div className="alert alert-info mb-4">
              <strong>🏆 Your Judged Scorecards</strong>
              <br />
              Scorecards are grouped by category. Select any entry to update its scorecard.
            </div>

            {pointsVotesGroupedByCategory.length === 0 ? (
              <div className="alert alert-warning">
                No scorecards submitted yet.
              </div>
            ) : (
              pointsVotesGroupedByCategory.map((group) => (
                <div key={group.cat.id} className="mb-4">

                  <h4 className="mb-2">{group.categoryName}</h4>

                  <div className="list-group">

                    {group.votes.map((vote) => (
                      <div key={vote.id} className="list-group-item">

                        <div className="row align-items-start g-3">

                          <div className="col-12 col-md-8">
                            <Link
                              to={`/${eventslug}/vote/${vote.cid}`}
                              className="text-decoration-none"
                            >
                              <strong>#{vote.regcnt}</strong>
                              {" — "}
                              {vote.description}
                            </Link>

                            <div className="small text-muted">
                              {vote.name}
                            </div>

                            <div className="small mt-1">
                              <Link
                                to={`/${eventslug}/vote/${vote.cid}`}
                                className="text-decoration-underline"
                              >
                                ✏️ Update scorecard
                              </Link>
                            </div>
                          </div>

                          <div className="col-12 col-md-4 text-md-end">

                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => toggleScorecard(vote.id)}
                            >
                              {(vote.totalPoints || 0).toFixed(2)}
                              {" / "}
                              {vote.maxBallotPoints || 100}

                              {" "}

                              {expandedScorecards[vote.id]
                                ? "▲"
                                : "▼"}
                            </button>
                          </div>

                        </div>

                        {expandedScorecards[vote.id] &&
                          (vote.scores || []).length > 0 && (
                          <div className="row mt-3 g-3">

                            {(vote.scores || []).map((score) => (
                              <div
                                key={score.factorId}
                                className="col-12 col-md-6 col-xl-4"
                              >
                                <div className="border rounded p-3 h-100 bg-light">

                                  <div className="d-flex justify-content-between align-items-start gap-2">
                                    <strong>{score.factorName}</strong>

                                    <span className="badge bg-secondary">
                                      {score.pointsAwarded}
                                      {" / "}
                                      {score.maxPoints}
                                    </span>
                                  </div>

                                  {(score.checkedHighlights || []).some(
                                    (highlight) => Number(highlight.maxPoints || 0) > 0
                                  ) && (
                                    <div className="mt-2">

                                      {(score.checkedHighlights || []).map((highlight) => {
                                        if (Number(highlight.maxPoints || 0) <= 0) {
                                          return null;
                                        }

                                        return (
                                          <div
                                            key={highlight.highlightId}
                                            className="small text-muted border-top pt-2 mt-2"
                                          >
                                            <div>{highlight.highlightName}</div>
                                            <div>
                                              {highlight.pointsAwarded || 0}
                                              {" / "}
                                              {highlight.maxPoints}
                                            </div>
                                          </div>
                                        );
                                      })}

                                    </div>
                                  )}

                                </div>
                              </div>
                            ))}

                          </div>
                        )}

                      </div>
                    ))}

                  </div>
                </div>
              ))
            )}

          </div>
        )}

        {votingType === "standard" && (
          <>
            {/* ðŸ† Best in Show */}
            {bestInShowEnabled && (
              <div className="mt-4 p-3 border rounded bg-light">
                <h4>🏆 Best in Show</h4>

                {bestInShowVote ? (
                  <div className="mt-2">
                    ✅ You voted for <strong>#{bestInShowVote.regcnt}</strong>
                    {" — "}
                    <strong>{bestInShowVote.description}</strong>
                    {" by "}
                    <strong>{bestInShowVote.name}</strong>
                  </div>
                ) : (
                  <div className="alert alert-info mt-2">
                    👀 You haven’t picked your Best in Show yet! Pro tip: Pick your Best of Show as you go. You can submit for a new Best of Show when you see it.
                  </div>
                )}
              </div>
            )}

            {/* ðŸ—³ï¸ People's Choice Ballot */}
            <div className="printable-area mt-4">

              <div className="alert alert-info mb-4">
                <strong>🗳️ Your People's Choice Votes</strong>
                <br />
                Votes are grouped by category. Select any submitted vote to view or update it.
              </div>

              {sortedCategories.map((cat) => {
                const vote = votesByCat[String(cat.id)];

                return (
                  <div key={cat.id} className="mb-4">

                    <h4 className="mb-2">{getCategoryName(cat)}</h4>

                    <div className="list-group">

                      {!vote ? (
                        <div className="list-group-item">
                          <div className="text-danger">
                            ⚠️ No vote submitted for this category.
                          </div>
                        </div>
                      ) : (
                        <div className="list-group-item">
                          <div className="row align-items-center g-3">

                            <div className="col-12 col-md-2">
                              <span className="badge bg-success fs-6">
                                Voted
                              </span>
                            </div>

                            <div className="col-12 col-md-7">
                              <Link
                                to={`/${eventslug}/vote/${vote.cid}`}
                                className="text-decoration-none"
                              >
                                <strong>
                                  #{vote.regcnt} — {vote.description}
                                </strong>
                              </Link>

                              <div className="small text-muted">
                                {vote.name || "Unknown Owner"}
                              </div>

                              <div className="small text-muted">
                                Voted at:{" "}
                                {vote.createdAt?.toDate?.()?.toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }) || "—"}
                              </div>
                            </div>

                            <div className="col-12 col-md-3 text-md-end">
                              <Link
                                to={`/${eventslug}/vote/${vote.cid}`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                View / Update
                              </Link>
                            </div>

                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                );
              })}

            </div>
          </>
        )}

        </div>
      </section>
    </RequireEventId>
  );
  
};

export default Ballot;
